import React, { useRef, useState, useEffect } from "react";
import {
  Avatar as UIAvatar,
  AvatarImage,
  AvatarFallback,
} from "@/components/ui/avatar";
import { Camera } from "lucide-react";

const cn = (...classes: (string | undefined)[]) => {
  return classes.filter(Boolean).join(" ");
};

const avatarVariants = {
  static: "relative",
  interactive: "group relative cursor-pointer hover:opacity-95",
};

const sizeVariants = {
  xs: "h-5 w-5",
  sm: "h-10 w-10",
  md: "h-16 w-16",
  lg: "h-32 w-32",
};

interface AvatarProps {
  variant?: keyof typeof avatarVariants;
  size?: keyof typeof sizeVariants;
  src?: string;
  onChange?: (file: File | null) => void;
  className?: string;
}

const Avatar = ({
  variant = "static",
  size = "md",
  src = "/api/placeholder",
  onChange,
  className,
}: AvatarProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState(src);
  const [error, setError] = useState<string | null>(null);

  // Update preview URL when src prop changes
  useEffect(() => {
    setPreviewUrl(src);
  }, [src]);

  const validateFile = (file: File): boolean => {
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return false;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError("Image must be less than 5MB");
      return false;
    }

    setError(null);
    return true;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (validateFile(file)) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        onChange?.(file);
      }
      event.target.value = "";
    }
  };

  const handleClick = () => {
    if (variant === "interactive") {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className='relative inline-block'>
      <div
        className={cn(avatarVariants[variant], className)}
        onClick={handleClick}
      >
        <UIAvatar
          className={cn(
            "ring-2 ring-white ring-offset-background",
            sizeVariants[size]
          )}
        >
          <AvatarImage src={previewUrl} alt='Avatar' className='object-cover' />
          <AvatarFallback className='bg-gray-200'>
            {/* <Image 
              src="/avatar.svg" 
              alt="Default Avatar"
              width={size === "lg" ? 40 : 24}
              height={size === "lg" ? 40 : 24}
              className="transition-opacity"
            /> */}
          </AvatarFallback>
        </UIAvatar>

        {variant === "interactive" && (
          <>
            <div className='absolute inset-0 flex items-center justify-center bg-white/60 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200'>
              <Camera
                className={cn(
                  "text-gray-800 transition-transform group-hover:scale-110",
                  size === "lg" ? "w-10 h-10" : "w-6 h-6"
                )}
              />
            </div>
            <input
              type='file'
              ref={fileInputRef}
              onChange={handleFileChange}
              accept='image/*'
              className='hidden'
              aria-label='Upload avatar image'
            />
          </>
        )}
      </div>
      {error && (
        <div className='absolute -bottom-6 left-0 right-0 text-center text-sm text-red-500'>
          {error}
        </div>
      )}
    </div>
  );
};

export default Avatar;
